import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useCookieConsent } from '../contexts/CookieConsentContext'

interface CookiePreferenceModalProps {
  isOpen: boolean
  onClose: () => void
}

const CookiePreferenceModal = ({ isOpen, onClose }: CookiePreferenceModalProps) => {
  const { preferences, updatePreferences } = useCookieConsent()

  const [functional, setFunctional] = useState(preferences?.functional ?? false)
  const [analytics, setAnalytics] = useState(preferences?.analytics ?? false)
  const [marketing, setMarketing] = useState(preferences?.marketing ?? false)

  useEffect(() => {
    if (preferences) {
      setFunctional(preferences.functional)
      setAnalytics(preferences.analytics)
      setMarketing(preferences.marketing)
    }
  }, [preferences])

  const handleSave = () => {
    updatePreferences({
      functional,
      analytics,
      marketing
    })
    onClose()
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Cookie Preferences
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                aria-label="Close cookie preferences"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              We use cookies to enhance your browsing experience and analyze website traffic.
              You can choose which types of cookies you want to allow.
            </p>

            <div className="space-y-4">
              {/* Essential Cookies */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Essential Cookies
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Required for the website to function properly. These cannot be disabled.
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 bg-primary-600 rounded-full flex items-center px-1">
                      <div className="w-4 h-4 bg-white rounded-full ml-auto"></div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Examples: Session cookies, security tokens, theme preferences
                </p>
              </div>

              {/* Functional Cookies */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Functional Cookies
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Remember your preferences and settings for a better experience.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setFunctional(!functional)}
                      className={`w-12 h-6 rounded-full transition-colors duration-200 flex items-center px-1 ${
                        functional ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                      aria-label={functional ? 'Disable functional cookies' : 'Enable functional cookies'}
                    >
                      <div
                        className={`w-4 h-4 bg-white rounded-full transition-transform duration-200 ${
                          functional ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Examples: Language preferences, saved filters, customized layouts
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Analytics Cookies
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Help us understand how visitors interact with our website (Matomo Analytics).
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setAnalytics(!analytics)}
                      className={`w-12 h-6 rounded-full transition-colors duration-200 flex items-center px-1 ${
                        analytics ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                      aria-label={analytics ? 'Disable analytics cookies' : 'Enable analytics cookies'}
                    >
                      <div
                        className={`w-4 h-4 bg-white rounded-full transition-transform duration-200 ${
                          analytics ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Examples: Page views, time on site, bounce rate, traffic sources
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Marketing Cookies
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Used to deliver relevant advertisements and track campaign performance.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setMarketing(!marketing)}
                      className={`w-12 h-6 rounded-full transition-colors duration-200 flex items-center px-1 ${
                        marketing ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                      aria-label={marketing ? 'Disable marketing cookies' : 'Enable marketing cookies'}
                    >
                      <div
                        className={`w-4 h-4 bg-white rounded-full transition-transform duration-200 ${
                          marketing ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Examples: Ad targeting, conversion tracking, remarketing
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default CookiePreferenceModal
